import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import {
  WIKI_PAGES,
  WIKI_SECTIONS,
  extractHeadings,
  getAdjacentPages,
  getWikiPage,
  getWikiSectionPages,
  searchWikiPages,
} from '../content/wiki';
import WikiMarkdown from '../components/WikiMarkdown';

function WikiSearch({ onNavigate }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const results = useMemo(() => searchWikiPages(query).slice(0, 8), [query]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const tag = event.target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="wiki-search">
      <input
        ref={inputRef}
        className="form-input wiki-search-input"
        placeholder="Search docs… (/ to focus)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {query.trim() && (
        <div className="wiki-search-results">
          {results.length === 0 ? (
            <div className="wiki-search-empty">No matching pages.</div>
          ) : (
            results.map((page) => (
              <Link
                key={page.slug}
                to={`/docs/${page.slug}`}
                className="wiki-search-result"
                onClick={() => {
                  setQuery('');
                  onNavigate?.();
                }}
              >
                <strong>{page.title}</strong>
                <span>{page.description}</span>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function WikiHome() {
  return (
    <div className="wiki-home">
      <div className="wiki-home-hero">
        <h1>Platform documentation</h1>
        <p>
          Operator guides for intelligence enrichment, investigations, engagement workflows,
          and platform reference material.
        </p>
      </div>

      <div className="wiki-home-grid">
        {WIKI_SECTIONS.map((section) => (
          <section key={section.id} className="wiki-home-section card">
            <div className="card-body">
              <div className="wiki-home-section-head">
                <span className="wiki-section-icon">{section.icon}</span>
                <h2>{section.title}</h2>
              </div>
              <ul className="wiki-home-links">
                {getWikiSectionPages(section.id).map((page) => (
                  <li key={page.slug}>
                    <Link to={`/docs/${page.slug}`}>{page.title}</Link>
                    <span>{page.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}
      </div>

      <div className="wiki-home-quick card">
        <div className="card-body">
          <h2>Quick links</h2>
          <div className="wiki-quick-links">
            <Link to="/intelligence/search" className="wiki-quick-link">Run a search</Link>
            <Link to="/cases" className="wiki-quick-link">Open cases</Link>
            <Link to="/docs/relationships-graph" className="wiki-quick-link">Investigation graph</Link>
            <Link to="/docs/keyboard-shortcuts" className="wiki-quick-link">Keyboard shortcuts</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WikiArticle() {
  const { slug } = useParams();
  const page = getWikiPage(slug);
  const body = page?.body?.replace(/^#\s+.+\n+/, '') || '';
  const headings = page ? extractHeadings(body) : [];
  const { prev, next } = getAdjacentPages(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!page) {
    return (
      <div className="wiki-not-found">
        <h1>Page not found</h1>
        <p>The documentation page <code>{slug}</code> does not exist.</p>
        <Link to="/docs" className="btn btn-primary btn-sm">Back to docs home</Link>
      </div>
    );
  }

  const sectionMeta = WIKI_SECTIONS.find((section) => section.id === page.section);

  return (
    <div className="wiki-article-layout">
      <div className="wiki-article-main">
        <nav className="wiki-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/docs">Documentation</Link>
          <span>/</span>
          <span>{sectionMeta?.title}</span>
          <span>/</span>
          <span>{page.title}</span>
        </nav>

        <header className="wiki-article-header">
          <h1>{page.title}</h1>
          <div className="wiki-article-meta">
            <span className="wiki-section-badge">{sectionMeta?.icon} {sectionMeta?.title}</span>
            {page.tags?.slice(0, 3).map((tag) => (
              <span key={tag} className="wiki-tag">{tag}</span>
            ))}
          </div>
          <p className="wiki-article-description">{page.description}</p>
        </header>

        <WikiMarkdown content={body} />

        <footer className="wiki-article-footer">
          <div className="wiki-article-nav">
            {prev ? (
              <Link to={`/docs/${prev.slug}`} className="wiki-page-nav-link prev">
                <span>Previous</span>
                <strong>{prev.title}</strong>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/docs/${next.slug}`} className="wiki-page-nav-link next">
                <span>Next</span>
                <strong>{next.title}</strong>
              </Link>
            ) : <span />}
          </div>
        </footer>
      </div>

      {headings.length > 0 && (
        <aside className="wiki-toc" aria-label="On this page">
          <div className="wiki-toc-title">On this page</div>
          <ul>
            {headings.map((heading) => (
              <li key={heading.id} className={`wiki-toc-item level-${heading.level}`}>
                <a
                  href={`#${heading.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  );
}

export default function WikiLayout() {
  return (
    <div className="wiki-shell">
      <aside className="wiki-sidebar">
        <div className="wiki-sidebar-head">
          <Link to="/docs" className="wiki-sidebar-title">
            <span>📚</span>
            <div>
              <strong>Documentation</strong>
              <span>Platform wiki</span>
            </div>
          </Link>
        </div>

        <WikiSearch />

        <nav className="wiki-sidebar-nav">
          {WIKI_SECTIONS.map((section) => {
            const pages = getWikiSectionPages(section.id);
            return (
              <div key={section.id} className="wiki-nav-section">
                <div className="wiki-nav-section-title">
                  <span>{section.icon}</span>
                  {section.title}
                </div>
                {pages.map((page) => (
                  <NavLink
                    key={page.slug}
                    to={`/docs/${page.slug}`}
                    className={({ isActive }) => `wiki-nav-link ${isActive ? 'active' : ''}`}
                  >
                    {page.title}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="wiki-sidebar-foot">
          <span>{WIKI_PAGES.length} articles</span>
        </div>
      </aside>

      <main className="wiki-content">
        <Outlet />
      </main>
    </div>
  );
}
